/** 纸张、剪口与折线的 SVG 绘制。折叠态与展开态共用同一份几何数据。 */
import { useMemo } from 'react';
import type { CutShape, Project, Vec } from '../domain/types';
import { cutToPolygon, computeUnfold, foldedView, maxFinishedSize, paperGeometry } from '../domain/folds/geometry';
import { getFold } from '../domain/folds/definitions';
import { multiPolyToPaths } from '../domain/geometry/boolean';
import { polygonToPath } from '../domain/geometry/polygon';
import { viewBoxFromBounds } from './view';
import { boundsOf } from '../domain/geometry/polygon';

const CREASE_STROKE = '#8a8a8a';
const CUT_FILL = 'rgba(120,120,120,0.28)';
const CUT_STROKE = '#4b4b4b';

export interface FoldLineProps {
  creaseAnglesDeg: number[];
  radius: number;
}

/** 折线：过中心的直线，角度按 fold 定义。 */
export function FoldLines({ creaseAnglesDeg, radius }: FoldLineProps) {
  return (
    <g className="fold-lines" pointerEvents="none">
      {creaseAnglesDeg.map((deg, i) => {
        const rad = (deg * Math.PI) / 180;
        const x = Math.cos(rad) * radius;
        const y = Math.sin(rad) * radius;
        return (
          <line
            key={`${deg}-${i}`}
            x1={-x}
            y1={-y}
            x2={x}
            y2={y}
            stroke={CREASE_STROKE}
            strokeWidth={0.5}
            strokeDasharray="3 2"
            vectorEffect="non-scaling-stroke"
          />
        );
      })}
    </g>
  );
}

/** 折叠态画布内容：可编辑区域 + 剪口 + 折线 + 正在绘制的草稿。 */
export function FoldedScene({
  project,
  draft,
  selectedId,
  showHandles,
  onHandleDown,
}: {
  project: Project;
  draft?: Vec[] | null;
  selectedId?: string | null;
  showHandles?: boolean;
  onHandleDown?: (e: React.PointerEvent, cutId: string) => void;
}) {
  const geometry = useMemo(() => paperGeometry(project.foldId, project.finishedSize), [project.foldId, project.finishedSize]);
  const view = useMemo(() => foldedView(project.foldId, project.finishedSize, project.cuts), [project]);
  const radius = useMemo(() => Math.max(...geometry.paper.map((p) => Math.hypot(p.x, p.y))) * 1.05, [geometry]);

  return (
    <g>
      {/* 折后纸张（可编辑区域） */}
      <path
        d={polygonToPath(geometry.editable)}
        fill="#faf7f2"
        stroke="#c9c2b6"
        strokeWidth={0.6}
        vectorEffect="non-scaling-stroke"
      />
      <defs>
        <clipPath id="editable-clip">
          <path d={polygonToPath(geometry.editable)} />
        </clipPath>
      </defs>
      {project.guides.showGrid && <GridLines bounds={geometryBounds(geometry.editable)} stepMm={Math.max(5, project.guides.snapMm * 5)} />}
      {project.guides.showFoldLines && <FoldLines creaseAnglesDeg={geometry.creaseAnglesDeg} radius={radius} />}
      {/* 剪口（裁切在可编辑区内） */}
      <g clipPath="url(#editable-clip)">
        {project.cuts.map((cut) => (
          <CutPath key={cut.id} cut={cut} selected={cut.id === selectedId} />
        ))}
        {view.cutRegion.length > 0 && (
          <path d={multiPolyToPaths(view.cutRegion)} fill={CUT_FILL} stroke={CUT_STROKE} strokeWidth={0.5} />
        )}
        {draft && draft.length > 1 && (
          <path
            d={polygonToPath(draft)}
            fill="rgba(217,72,59,0.18)"
            stroke="#d9483b"
            strokeWidth={0.8}
            strokeDasharray="2 1.5"
          />
        )}
        {draft && draft.length === 1 && <circle cx={draft[0].x} cy={draft[0].y} r={0.9} fill="#d9483b" />}
      </g>
      {/* 选中状态与缩放手柄 */}
      {showHandles && selectedId && (
        <SelectionHandles project={project} selectedId={selectedId} onHandleDown={onHandleDown} />
      )}
    </g>
  );
}

/** 毫米网格：只在打开"显示网格"时绘制，步长按吸附精度放大到可读尺寸。 */
function GridLines({ bounds, stepMm }: { bounds: { minX: number; minY: number; maxX: number; maxY: number }; stepMm: number }) {
  const xs: number[] = [];
  const ys: number[] = [];
  for (let x = Math.ceil(bounds.minX / stepMm) * stepMm; x <= bounds.maxX; x += stepMm) xs.push(x);
  for (let y = Math.ceil(bounds.minY / stepMm) * stepMm; y <= bounds.maxY; y += stepMm) ys.push(y);
  return (
    <g className="grid-lines" pointerEvents="none" opacity={0.5}>
      {xs.map((x) => (
        <line key={'gx' + x} x1={x} y1={bounds.minY} x2={x} y2={bounds.maxY} stroke="#e3dccd" strokeWidth={0.3} vectorEffect="non-scaling-stroke" />
      ))}
      {ys.map((y) => (
        <line key={'gy' + y} x1={bounds.minX} y1={y} x2={bounds.maxX} y2={y} stroke="#e3dccd" strokeWidth={0.3} vectorEffect="non-scaling-stroke" />
      ))}
    </g>
  );
}

function geometryBounds(poly: Vec[]) {
  return boundsOf(poly);
}

function CutPath({ cut, selected }: { cut: CutShape; selected: boolean }) {
  const poly = cutToPolygon(cut);
  return (
    <path
      d={polygonToPath(poly)}
      fill={selected ? 'rgba(217,72,59,0.22)' : 'rgba(120,120,120,0.16)'}
      stroke={selected ? '#d9483b' : '#9a9a9a'}
      strokeWidth={selected ? 0.8 : 0.4}
      vectorEffect="non-scaling-stroke"
      className={selected ? 'cut selected' : 'cut'}
    />
  );
}

function SelectionHandles({
  project,
  selectedId,
  onHandleDown,
}: {
  project: Project;
  selectedId: string;
  onHandleDown?: (e: React.PointerEvent, cutId: string) => void;
}) {
  const cut = project.cuts.find((c) => c.id === selectedId);
  if (!cut) return null;
  const poly = cutToPolygon(cut);
  const b = boundsOf(poly);
  const handle =
    cut.kind === 'circle'
      ? { x: cut.center.x + cut.radius, y: cut.center.y }
      : cut.kind === 'rect'
        ? { x: b.maxX, y: b.maxY }
        : cut.kind === 'triangle'
          ? { x: cut.center.x + cut.radius, y: cut.center.y }
          : null;
  return (
    <g pointerEvents="all">
      <path d={polygonToPath(poly)} fill="none" stroke="#d9483b" strokeWidth={0.9} strokeDasharray="2 1.5" vectorEffect="non-scaling-stroke" />
      {handle && (
        <circle
          cx={handle.x}
          cy={handle.y}
          r={2.2}
          fill="#d9483b"
          stroke="#fff"
          strokeWidth={0.6}
          vectorEffect="non-scaling-stroke"
          style={{ cursor: 'nwse-resize' }}
          onPointerDown={(e) => onHandleDown?.(e, cut.id)}
        />
      )}
    </g>
  );
}

/** 展开态：纸张 + 洞，使用 evenodd 填充规则。 */
export function UnfoldedScene({ project, paperColor }: { project: Project; paperColor?: string }) {
  const result = useMemo(
    () => computeUnfold(project.foldId, project.finishedSize, project.cuts),
    [project.foldId, project.finishedSize, project.cuts],
  );
  const geometry = useMemo(() => paperGeometry(project.foldId, project.finishedSize), [project.foldId, project.finishedSize]);
  const d = useMemo(
    () => polygonToPath(geometry.paper) + (result.holes.length ? multiPolyToPaths(result.holes) : ''),
    [geometry.paper, result.holes],
  );
  return (
    <g>
      <path
        d={d}
        fill={paperColor ?? project.paperColor}
        fillRule="evenodd"
        stroke="#00000022"
        strokeWidth={0.4}
        vectorEffect="non-scaling-stroke"
      />
    </g>
  );
}

/** 供缩略图/预览使用的自适应 viewBox。 */
export function useSceneViewBox(project: Project, pad = 10): string {
  return useMemo(() => {
    const geometry = paperGeometry(project.foldId, project.finishedSize);
    return viewBoxFromBounds(boundsOf(geometry.paper), pad);
  }, [project.foldId, project.finishedSize, pad]);
}

export function useMaxSizeHint(project: Project): string {
  const fold = getFold(project.foldId);
  const max = maxFinishedSize(fold);
  if (fold.paper === 'square') return `最大外接直径 ${max.value[0].toFixed(1)} mm`;
  return `最大 ${max.value[0].toFixed(0)} × ${max.value[1].toFixed(0)} mm`;
}